import { useId, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { OrganizationSelections } from '@socialgoodsoftware/libraries/services/organization-selection';
import { topBarIconButtonSx } from './constants';
import { useProductSwitcher } from './product-switcher-context';
import { ButtonPrimary } from '../inputs/buttons/primary/primary';
import { useCopyToClipboard } from '../toast/use-copy-to-clipboard';

import type { MouseEvent, ReactNode } from 'react';
import type { AppShellAccount, OrganizationSelection } from './types';

type ShareLinkProps = {
  account: AppShellAccount | null;
  emitDocAttributes: boolean;
};

type ShareLinkMenuProps = ShareLinkProps & {
  organization: OrganizationSelection;
};

type OpenShare = {
  anchor: HTMLElement;
  url: string;
};

function getAccountBase(account: AppShellAccount | null): string {
  let base = window.location.origin;
  if (account && typeof account.accountHubBaseUrl === 'string' && account.accountHubBaseUrl.length > 0) {
    base = account.accountHubBaseUrl;
  }
  return base;
}

function ShareLinkMenu({ organization, account, emitDocAttributes }: ShareLinkMenuProps) {
  const [share, setShare] = useState<OpenShare | null>(null);
  const copy = useCopyToClipboard();
  const linkLabelId = useId();
  const docProps = emitDocAttributes ? { 'data-doc-action': 'open-share-link' } : {};

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setShare({
      anchor: event.currentTarget,
      url: OrganizationSelections.GetLink(getAccountBase(account), organization, window.location.href),
    });
  };

  const handleClose = () => {
    setShare(null);
  };

  const handleCopy = async () => {
    if (share !== null) {
      const url = share.url;
      setShare(null);
      await copy(url, { successTitle: 'Link copied', successDescription: `It opens this page in ${organization.name}` });
    }
  };

  let url = '';
  let anchor: HTMLElement | null = null;
  if (share !== null) {
    url = share.url;
    anchor = share.anchor;
  }

  return (
    <>
      <IconButton aria-label="Share this page" title="Share this page" onClick={handleOpen} {...docProps} sx={topBarIconButtonSx}>
        <ShareOutlinedIcon aria-hidden />
      </IconButton>
      <Popover
        open={share !== null}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': 'Share this link',
            sx: { mt: '6px', borderRadius: '8px', border: 1, borderColor: 'divider', boxShadow: 6 },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 2, width: 400, maxWidth: 'calc(100vw - 32px)' }}>
          <Typography id={linkLabelId} component="h2" sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>
            Link
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 0.5,
              pr: 0.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:hover .share-link-copy, &:focus-within .share-link-copy': { opacity: 1 },
            }}
          >
            <Box
              component="p"
              aria-labelledby={linkLabelId}
              tabIndex={0}
              sx={{
                m: 0,
                flex: 1,
                minWidth: 0,
                px: 1.25,
                py: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
                lineHeight: 1.5,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflowX: 'auto',
                userSelect: 'all',
                cursor: 'text',
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
              }}
            >
              {url}
            </Box>
            <IconButton
              aria-label="Copy link"
              title="Copy link"
              size="small"
              onClick={handleCopy}
              className="share-link-copy"
              sx={{ flexShrink: 0, opacity: 0, '&:focus-visible': { opacity: 1 } }}
            >
              <ContentCopyOutlinedIcon fontSize="small" aria-hidden />
            </IconButton>
          </Box>
          <ButtonPrimary type="button" onClick={handleCopy} sx={{ mt: 1.5 }}>
            Copy link
          </ButtonPrimary>
        </Box>
      </Popover>
    </>
  );
}

export function ShareLink({ account, emitDocAttributes }: ShareLinkProps) {
  const switcher = useProductSwitcher();
  let share: ReactNode = null;
  if (switcher !== null && switcher.selectedOrganization) {
    share = (
      <ShareLinkMenu organization={switcher.selectedOrganization} account={account} emitDocAttributes={emitDocAttributes} />
    );
  }
  return share;
}
